import { auth } from "../auth";
import {
  Brand,
  BrowserInstallRecommendation,
  EmptyState,
  LeagueIdentityHeader,
  NavButton,
} from "../components/shared";
import { offlineStore } from "../offlineDb";
import { Welcome } from "../screens/onboarding";
import {
  Bell,
  Building2,
  LogOut,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useState } from "react";

export function InviteNeeded({ account, online, install }) {
  const [section, setSection] = useState("player");
  const email =
    account?.username || account?.idTokenClaims?.email || "Signed-in player";
  return (
    <>
      <BrowserInstallRecommendation install={install} />
      <div className="mobile-shell unconnected-account">
        <LeagueIdentityHeader
          organizationName="Oche Player Account"
          online={online}
        />
        <main className="app-content">
          {section === "player" && (
            <section className="section-stack">
              <div className="greeting">
                <span>PLAYER DASHBOARD</span>
                <h1>Welcome to Oche</h1>
                <p>{email}</p>
              </div>
              <article className="detail-card">
                <header>
                  <UserRound />
                  <h2>Connect your player record</h2>
                </header>
                <p className="muted-copy">
                  Open the invitation from your captain to connect the exact
                  player record and continue joining the team. If you already
                  opened it, return to the original email and tap the invitation
                  link again.
                </p>
              </article>
              <div className="claim-proof">
                <ShieldCheck />
                <span>
                  We use the private invitation instead of matching by name,
                  preventing another player’s history from being connected to
                  your account.
                </span>
              </div>
            </section>
          )}
          {section === "notifications" && (
            <section className="section-stack notification-center">
              <div className="section-heading">
                <span className="kicker">YOUR UPDATES</span>
                <h1>Notifications</h1>
                <p>
                  Your league and team updates will remain available here after
                  your player record is connected.
                </p>
              </div>
              <EmptyState
                icon={<Bell />}
                title="No updates yet"
                text="Finish the invitation to connect your league notifications to this account."
              />
            </section>
          )}
          {section === "account" && (
            <section className="section-stack">
              <div className="profile-hero">
                <div className="avatar">{email[0]?.toUpperCase()}</div>
                <span className="kicker">SIGNED IN</span>
                <h1>Player account</h1>
                <p>{email}</p>
              </div>
              <div className="account-actions single-action">
                <button
                  onClick={async () => {
                    await offlineStore.clearForSignOut();
                    await auth.signOut();
                  }}
                >
                  <LogOut /> Sign out
                </button>
              </div>
            </section>
          )}
        </main>
        <nav
          className="bottom-nav account-bottom-nav"
          aria-label="Account navigation"
        >
          <NavButton
            active={section === "player"}
            onClick={() => setSection("player")}
            icon={<UserRound />}
            label="Player"
          />
          <NavButton
            active={section === "notifications"}
            onClick={() => setSection("notifications")}
            icon={<Bell />}
            label="Updates"
          />
          <NavButton
            active={section === "account"}
            onClick={() => setSection("account")}
            icon={<LogOut />}
            label="Account"
          />
        </nav>
      </div>
    </>
  );
}

export function MembershipRequired({ onSwitch }) {
  return (
    <main className="organization-screen">
      <Brand />
      <section>
        <span className="kicker">ONE MORE STEP</span>
        <h1>Connect your player record</h1>
        <p>
          Your sign-in can access this organization, but it is not connected to
          a registered player yet.
        </p>
        <div className="membership-steps">
          <b>If you are also a player</b>
          <span>
            In the Control Panel, open Players, select your player record, and
            create an invitation. Open that invitation link on this phone to
            connect the exact record.
          </span>
        </div>
        <div className="claim-proof">
          <ShieldCheck />
          <span>
            We do not link by name alone because spelling and duplicate names
            can connect the wrong statistics.
          </span>
        </div>
        <div className="membership-actions">
          <button onClick={onSwitch}>
            <Building2 /> Choose another organization
          </button>
          <button
            onClick={async () => {
              await offlineStore.clearForSignOut();
              await auth.signOut();
            }}
          >
            <LogOut /> Sign out
          </button>
        </div>
      </section>
    </main>
  );
}
